import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/Security';
import { TWO_FA_VERIFY_SUCCESS } from '../../redux/actions/authActions';
import { twoFAMethod, verifyTwoFA, setupTotp, confirmTotp } from '../../api/authApi';

const METHOD_CONFIG = {
    email: {
        label: 'Email',
        desc: '', // set dynamically from API email
        Icon: EmailOutlinedIcon,
    },
    totp: {
        label: 'Authenticator App (TOTP)',
        desc: 'Use any authenticator app',
        Icon: SecurityOutlinedIcon,
    },
};

export const getMethodOptions = (availableMethods = [], email = '') =>
    availableMethods.map((method) => ({
        value: method,
        ...METHOD_CONFIG[method],
        desc: method === 'email' ? `Send code to ${email}` : METHOD_CONFIG[method]?.desc,
    }));

export const otpInputSx = (digit, hasError) => ({
    '& input': { color: digit ? 'var(--teal-dark)' : undefined },
    '& .MuiOutlinedInput-root': {
        backgroundColor: digit ? 'var(--otp-filled-bg)' : 'var(--input-bg)',
        '& fieldset': { borderColor: hasError ? 'var(--error)' : digit ? 'var(--teal)' : 'var(--border)' },
        '&:hover fieldset': { borderColor: 'var(--teal)' },
        '&.Mui-focused fieldset': { borderColor: 'var(--teal)', boxShadow: 'var(--teal-shadow)' },
    },
});

export const use2FAHandlers = ({ otp, setOtp, setOtpError, setTimer, setActiveStep, setLoading, otpRefs, formFields, dispatch, loginRequest, method, setQrCodeUrl, setTotpStep, tempToken, totpStep, setTooManyAttempts, setResendLoading }) => {

    const handleSendCode = async () => {
        setLoading(true);
        try {
            const data = await twoFAMethod({ temp_token: tempToken, method });

            if (method === 'totp') {
                if (data.setup_required) {
                    // TOTP not configured — call setup API to get QR
                    const setupData = await setupTotp({ temp_token: tempToken });
                    setQrCodeUrl(setupData.qr_code_url);
                    setTotpStep('qr');
                } else {
                    // TOTP already configured — go straight to verify
                    setTotpStep('verify');
                }
                setActiveStep(2);
                return;
            }

            // SMS / Email
            setTimer(30);
            setActiveStep(2);
            setTimeout(() => otpRefs.current[0]?.focus(), 100);
        } catch (error) {
            const errorDetail = error?.errors?.[0]?.detail || 'Failed to send code. Please try again.';
            const isSessionExpired = errorDetail === 'Invalid or expired session' || error?.errors?.[0]?.code === 'invalid';
            setOtpError(errorDetail);
            if (isSessionExpired) {
                setTooManyAttempts(true); // reuse same flag to disable UI
            }
        } finally {
            setLoading(false);
        }
    };
    const handleOtpChange = (index, value) => {
        if (!/^\d?$/.test(value)) return;
        const next = [...otp]; next[index] = value; setOtp(next); setOtpError('');
        if (value && index < 5) otpRefs.current[index + 1]?.focus();
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) otpRefs.current[index - 1]?.focus();
    };

    const handleOtpPaste = (e) => {
        const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        if (text.length === 6) { setOtp(text.split('')); otpRefs.current[5]?.focus(); }
    };

    const handleVerify = async () => {
        const code = otp.join('');
        if (code.length !== 6) return;
        setLoading(true);
        setOtpError('');
        try {
            if (method === 'totp' && totpStep === 'qr') {
                // First time TOTP setup — confirm OTP
                await confirmTotp({ temp_token: tempToken, otp: code });
                // After confirm, call verify to get tokens
            }
            const data = await verifyTwoFA({ temp_token: tempToken, otp: code });
            setActiveStep(3);
            setTimeout(() => dispatch({
                type: TWO_FA_VERIFY_SUCCESS,
                payload: { ...data }
            }), 1500);
        } catch (error) {
            const errorDetail = error?.errors?.[0]?.detail || 'Invalid or expired code. Please try again.';
            const isTooManyAttempts = errorDetail === 'Too many OTP attempts. Please login again.';
            const isSessionExpired = errorDetail === 'Session expired' || error?.errors?.[0]?.code === 'session_expired';
            setOtpError(errorDetail);
            setOtp(['', '', '', '', '', '']);
            if (isTooManyAttempts || isSessionExpired) {
                setTooManyAttempts(true);
            } else {
                setTimeout(() => otpRefs.current[0]?.focus(), 50);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        setResendLoading(true);
        try {
            await twoFAMethod({ temp_token: tempToken, method });
            setTimer(30);
            setOtp(['', '', '', '', '', '']);
            setOtpError('');
            setTimeout(() => otpRefs.current[0]?.focus(), 50);
        } catch (error) {
            setOtpError('Failed to resend code. Please try again.');
        } finally {
            setResendLoading(false);
        }
    };

    return { handleSendCode, handleOtpChange, handleOtpKeyDown, handleOtpPaste, handleVerify, handleResend };
};